import React from 'react';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {refreshAnalysis} from 'analysis-tool/polling/refreshPipeline';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ToastMessage from 'nav/toasts/ToastMessage';
import * as TrayTaskActions from 'tray/TrayTaskActions';
import * as TrayTaskConstants from 'tray/TrayTaskConstants';
import {withLoadingScreen} from 'util/Promises';

const ANALYSIS_PROGRESS_BAR_POLLING_INTERVAL_MS = 1000;

function showInProgressAnalysis(
  trayTaskId: string,
  numSteps: number,
  numSucceededSteps: number,
  isSendingData: boolean
): void {
  TrayTaskActions.updateTrayTask(trayTaskId, {
    heading: (
      <>
        <ToastMessage
          body={isSendingData ? 'Sending data...' : 'Running...'}
          progress={(100 * numSucceededSteps) / numSteps}
        />
      </>
    ),
    progress: TrayTaskConstants.HIDE_PROGRESS,
  });
}

function showSucceededAnalysis(trayTaskId: string, analysisId: string): void {
  TrayTaskActions.updateTrayTask(trayTaskId, {
    progress: TrayTaskConstants.SUCCESS_PROGRESS,
    heading: <ToastMessage body="Automation completed" progress={100} />,
    onHeadingClick: () => {
      FileBrowserActions.openFile(analysisId);
    },
  });
}

function showFailedAnalysis(trayTaskId: string, analysisId: string): void {
  TrayTaskActions.updateTrayTask(trayTaskId, {
    progress: TrayTaskConstants.FAILURE_PROGRESS,
    error: <ToastMessage header="Automation failed" body="Open to see errors" />,
    actions: [
      {
        actionText: 'Open',
        onActionClick: () => {
          FileBrowserActions.openFile(analysisId);
        },
      },
    ],
  });
}

function showFailedSendDataAnalysis(
  trayTaskId: string,
  analysisId: string,
  failedSendDataStepId: string
): void {
  TrayTaskActions.updateTrayTask(trayTaskId, {
    progress: TrayTaskConstants.FAILURE_PROGRESS,
    error: <ToastMessage header="Automation failed to send data" />,
    actions: [
      {
        actionText: 'Retry send data',
        onActionClick: async () => {
          await withLoadingScreen(
            AnalysisActions.runAnalysisStep(analysisId, {stepId: failedSendDataStepId}),
            'Sending data...'
          );
          updateAnalysisProgressTrayTask(trayTaskId, analysisId);
        },
      },
      {
        actionText: 'Open',
        onActionClick: () => {
          FileBrowserActions.openFile(analysisId);
        },
      },
    ],
  });
}

function pollForUpdate(trayTaskId: string, analysisId: string): void {
  setTimeout(() => {
    updateAnalysisProgressTrayTask(trayTaskId, analysisId);
  }, ANALYSIS_PROGRESS_BAR_POLLING_INTERVAL_MS);
}

async function updateAnalysisProgressTrayTask(trayTaskId: string, analysisId: string): Promise<void> {
  const analysis = await refreshAnalysis(analysisId);
  const steps = analysis.execution.steps;

  const failedSteps = steps.filter((step) => step.statusCode === PipelineStepStatusCode.FAILED);
  const succeededSteps = steps.filter(
    (step) =>
      step.statusCode === PipelineStepStatusCode.SUCCEEDED ||
      step.statusCode === PipelineStepStatusCode.HALTED
  );
  const isSendingData = steps.some(
    (step) =>
      step.statusCode === PipelineStepStatusCode.RUNNING &&
      step.definition.stepType === PipelineStepType.SEND_TO_DOCUMENT
  );

  if (succeededSteps.length === steps.length) {
    showSucceededAnalysis(trayTaskId, analysisId);
  } else if (
    failedSteps.length === 1 &&
    failedSteps[0].definition.stepType === PipelineStepType.SEND_TO_DOCUMENT
  ) {
    showFailedSendDataAnalysis(trayTaskId, analysisId, failedSteps[0].id);
  } else if (failedSteps.length > 0) {
    showFailedAnalysis(trayTaskId, analysisId);
  } else {
    showInProgressAnalysis(trayTaskId, steps.length, succeededSteps.length, isSendingData);
    pollForUpdate(trayTaskId, analysisId);
  }
}

/**
 * Show a live-updating progress-bar toast as a newly-created analysis executes.
 */
export function showAnalysisProgressTrayTask(analysisId: string): void {
  const trayTaskId = TrayTaskActions.createTrayTask({
    heading: (
      <>
        <ToastMessage body="Automation starting..." progress={TrayTaskConstants.INDETERMINATE_PROGRESS} />
      </>
    ),
    progress: TrayTaskConstants.HIDE_PROGRESS,
  });

  updateAnalysisProgressTrayTask(trayTaskId, analysisId);
}
